/**
 * Content for /services/vibe-code-rescue/ — audit and remediation of software
 * built by prompting an AI rather than by engineering it.
 *
 * WHO THIS IS FOR (Jazz, 2026-09-13): someone whose app works in the demo and
 * breaks in production. Often a founder or an operations lead who built it
 * themselves, sometimes a company that took delivery from someone who did. They
 * are not shopping for a philosophy of software craft — they have a thing that is
 * live, or nearly live, and going wrong.
 *
 * WHY THE NAME. "Vibe Code Rescue" is not ours — it is the category term. Google's
 * AI Overview for this intent says, unprompted: "You can hire specialized services
 * like Vibe-Code Rescue to audit, fix, and stabilize applications built through AI
 * prompting." Competitors exist (vibecodefixers.com, zappstudios.us, kosmoweb.cz)
 * and all are foreign. No Malaysian provider held the term when this shipped.
 *
 * ============================ THE GUARANTEE ============================
 *
 * Approved by Jazz 2026-09-13, and it is a PUBLIC COMMERCIAL COMMITMENT — treat
 * the wording as load-bearing. Three decisions, all deliberate:
 *
 *   1. The AUDIT IS PAID. It is a real deliverable and, more importantly, it is
 *      where the acceptance criteria get written. Guaranteeing discovery on a
 *      codebase nobody has read yet would be guaranteeing an unknown.
 *   2. The FIX IS OUTCOME-GUARANTEED against those written criteria. Criteria met,
 *      we invoice. Criteria missed, no fee for the fix.
 *   3. ACCEPTANCE IS BY WRITTEN CRITERIA AGREED BEFORE THE FIX STARTS — numbered,
 *      checkable by both sides, pass/fail. Not our judgement (a guarantee we grade
 *      ourselves is not a guarantee) and not open-ended client discretion (which
 *      turns a scope dispute into an unpaid invoice).
 *
 * DO NOT soften this into "we aim to" or "subject to". A hedged guarantee reads
 * worse than none. DO NOT extend it to cover the audit. DO NOT publish a price —
 * same rule as everywhere else on the site, quotation on enquiry.
 *
 * THE REWRITE ESCAPE HATCH IS WHAT PROTECTS THE GUARANTEE. If the audit concludes
 * the code should be rebuilt rather than repaired, we say so and quote a rebuild
 * separately — we do not take the fix under guarantee on a codebase we have
 * already judged unsalvageable. Keep that prominent; it is also the strongest
 * trust signal on the page.
 *
 * SOURCING. The Anthropic figures below are from "Detecting and countering misuse
 * of AI: September 2026" (anthropic.com/threat-intelligence-report-september-2026,
 * published 2026-09-10, retrieved 2026-09-13). That report documents attackers
 * harvesting hardcoded secrets at scale. It does NOT discuss vibe coding, AI-written
 * code or code quality — those words appear zero times in it. The link between
 * "software shipped without secrets discipline" and "secrets harvested" is OUR
 * argument on THEIR evidence, and the copy must keep those halves visibly separate.
 * Never write "Anthropic says vibe coding is insecure."
 */

export interface RescueStage {
  number: string;
  name: string;
  outcome: string;
  detail: string;
  deliverables: string[];
  commercial: string;
}

export const rescueStages: RescueStage[] = [
  {
    number: "01",
    name: "Audit and discovery",
    outcome: "You find out what is actually wrong, and whether it is worth fixing.",
    detail:
      "We read the code, run it, and try to break it the way a stranger would. The output is a written report you own — including, where that is the answer, a recommendation not to spend money repairing this at all. The audit also produces the acceptance criteria the next stage is measured against, which is why it comes first and why it is a paid piece of work rather than a sales call.",
    deliverables: [
      "Secrets and credentials audit — what is exposed in the shipped bundle, the repository and the client-side code",
      "Authentication and access review — which endpoints can be called by someone who should not be able to",
      "Architecture check — whether the data model and structure can carry where you are going, or need migrating",
      "The failure inventory — what breaks, under what conditions, ranked by what it costs you",
      "A prioritised roadmap with effort against each item, and the acceptance criteria for a fix",
    ],
    commercial: "Fixed scope, fixed fee, quoted on enquiry. You keep the report either way.",
  },
  {
    number: "02",
    name: "Fix and rectify",
    outcome: "The agreed criteria are met, or you do not pay for this stage.",
    detail:
      "We work to the numbered criteria written during the audit — these bugs closed, these endpoints authenticated, these secrets rotated out of the bundle, this load sustained. Both sides agreed them before a line was changed, so whether they are met is checkable rather than arguable. Meet them and we invoice. Miss them and there is no fee for the fix.",
    deliverables: [
      "The criteria closed out one by one, each demonstrable",
      "Secrets moved out of the code and rotated, with the old ones treated as already compromised",
      "Tests around the parts that broke, so the next change does not re-break them",
      "A deploy path that can be repeated and rolled back",
      "A handover your own people or your next developer can pick up",
    ],
    commercial:
      "Priced against the audit's criteria. Criteria met, we invoice. Criteria not met, no fee for this stage.",
  },
];

/**
 * Why these applications break. These are the patterns we see and that the wider
 * industry reports consistently — stated as our observation, not attributed to any
 * single source we have not verified end to end.
 */
export const failureModes = [
  {
    title: "The fix spiral",
    body: "Asking the AI to fix one bug quietly breaks three other things, because nothing in the codebase is isolated and nothing has tests holding it in place. Each round of fixing makes the next round harder. Most people call us somewhere in the third or fourth round, when the app is further from working than it was a week ago.",
  },
  {
    title: "Secrets in the shipped code",
    body: "API keys, database credentials and tokens end up hardcoded in the bundle, in the repository, or in client-side code where anyone can read them. It usually works fine — right up until it is found, and finding it is no longer manual.",
  },
  {
    title: "Authentication that only looks right",
    body: "The login screen works, so the app feels secure. Underneath, endpoints accept requests without checking who is asking, and row-level rules are either absent or misconfigured. Nothing fails visibly, which is exactly the problem.",
  },
  {
    title: "The logic nobody wrote",
    body: "Payment webhooks that never retry, error paths that swallow failures silently, edge cases that were never considered because nobody asked for them. Under real traffic these do not throw errors; they just quietly lose orders, or money, or data.",
  },
];

/**
 * The Anthropic evidence. Their figures, our inference, kept apart — see the
 * sourcing note in the header. This exists because the scale is genuinely new and
 * it changes the risk calculation on a shipped secret from "unlikely" to "when".
 */
export const harvestingEvidence = {
  heading: "Why exposed secrets stopped being a theoretical risk",
  body: "In September 2026 Anthropic published its threat intelligence report covering activity it disrupted between December 2025 and August 2026. One operation ran a credential-harvesting pipeline across ten cloud servers that mass-downloaded 1.8 million Android applications, decompiled them, and scanned them for hardcoded secrets automatically. A parallel stream collected stolen repository access tokens. Everything found was validated in batches — tested against live systems, graded for resale value — before use.",
  ourPoint:
    "That report is about attackers, not about how the software was written; it does not discuss AI-assisted development at all. The inference is ours, and it is simple. A hardcoded key used to be exposed only if somebody chose to look at your particular app. At 1.8 million applications scanned by machine, nobody is choosing. If the key is in the bundle, it is in the dragnet.",
  sourceLabel: "Anthropic, Detecting and countering misuse of AI, September 2026",
  sourceUrl: "https://www.anthropic.com/threat-intelligence-report-september-2026",
};

export const rewriteNote = {
  heading: "When we tell you to rebuild instead",
  body: "Sometimes the honest answer from the audit is that repairing this costs more than starting again, and we will say so in the report rather than take the work. That is also why the guarantee sits on the fix and not the audit: we do not promise an outcome on a codebase we have already judged unsalvageable. If a rebuild is the right call we will quote it separately, and you are free to take the audit report to someone else.",
};

export const guaranteeNote =
  "The audit is a fixed-scope paid piece of work and you keep the report whatever you decide next. The fix stage is measured against the numbered criteria written into that report and agreed by both sides before any code changes. Meet them and we invoice. Miss them and you do not pay for the fix.";

export const vibeCodeRescueFaqs = [
  {
    question: "What is vibe code rescue?",
    answer:
      "Auditing and repairing software that was built by prompting an AI rather than by engineering it. Typically the application works in a demo and fails in production — secrets exposed in the shipped code, endpoints that do not check who is calling them, payment and error paths that were never written, and a fix spiral where each AI-assisted repair breaks something else. We audit it, tell you whether it is worth repairing, and fix it against criteria agreed in writing first.",
  },
  {
    question: "What does it cost to fix a vibe-coded app in Malaysia?",
    answer:
      "The audit is fixed-scope and fixed-fee, quoted on enquiry once we know roughly how large the application is and what it touches. The fix is priced against the criteria the audit produces, because until someone has read the code any number is a guess. We do not publish a price for either, and anyone who quotes you a fix before reading the code is guessing too.",
  },
  {
    question: "What if the fix does not work?",
    answer:
      "Then you do not pay for it. The audit produces numbered acceptance criteria — these bugs closed, these endpoints authenticated, these secrets rotated out of the bundle, this load sustained — and both sides agree them before any code changes. Whether they are met is checkable rather than a matter of opinion. If they are not met, there is no fee for the fix stage. The audit is paid separately and is not covered by that, because the audit is where the criteria come from.",
  },
  {
    question: "Can you fix it if we built it ourselves with AI?",
    answer:
      "That is most of this work, and there is nothing embarrassing about it. Building something that works by prompting an AI is a genuine achievement and it is how a lot of useful software now starts. What it does not give you is the part that keeps it standing under real users — tests, secrets handling, access control, a deploy you can roll back. That is the part we add.",
  },
  {
    question: "Will you tell us if it is not worth rescuing?",
    answer:
      "Yes, in the report, and we would rather do that than take the money. If repairing it costs more than rebuilding it we will show you the comparison and quote a rebuild separately. You keep the audit report either way and you are free to take it to another developer.",
  },
  {
    question: "How do we avoid needing this again?",
    answer:
      "Learn the part the AI does not do for you. Our Vibe Engineering courses cover exactly the gap this service exists to fill — tests as the contract for what working means, environments and secrets, reading a diff before you accept it, and a deploy pipeline from staging to production. Most clients who go through the rescue send someone on the course afterwards, which is the outcome we would prefer.",
  },
];
